import { v4 as uuid } from 'uuid'

import { entityUrl, EntityUrlFragment } from 'src/components/TableViews/EntityDisplay'
import {
  UniversalSearchOption,
  UniversalSearchOptionType,
} from 'src/components/UniversalSearchModal/util'
import { EntityLayerTypes, getUniversalSearchEntitySubtitle } from 'src/utils/entityDisplayHelpers'

export type UniversalSearchEntityForOptions = EntityUrlFragment & {
  name: string
  addressDisplayName?: string | null
}

type UniversalSearchEntitiesByType = {
  addressEntities: UniversalSearchEntityForOptions[]
  parcelEntities: UniversalSearchEntityForOptions[]
  otherEntities: UniversalSearchEntityForOptions[]
}

const entityResultsKeyBySearchOptionType: Record<
  | UniversalSearchOptionType.Address
  | UniversalSearchOptionType.Parcel
  | UniversalSearchOptionType.Other,
  keyof UniversalSearchEntitiesByType
> = {
  [UniversalSearchOptionType.Address]: 'addressEntities',
  [UniversalSearchOptionType.Parcel]: 'parcelEntities',
  [UniversalSearchOptionType.Other]: 'otherEntities',
}

const layerTypeBySearchOptionType: Record<
  | UniversalSearchOptionType.Address
  | UniversalSearchOptionType.Parcel
  | UniversalSearchOptionType.Other,
  (typeof EntityLayerTypes)[keyof typeof EntityLayerTypes]
> = {
  [UniversalSearchOptionType.Address]: EntityLayerTypes.Address,
  [UniversalSearchOptionType.Parcel]: EntityLayerTypes.Parcel,
  [UniversalSearchOptionType.Other]: EntityLayerTypes.Other,
}

const entitySearchOptionTypes: Array<
  | UniversalSearchOptionType.Address
  | UniversalSearchOptionType.Parcel
  | UniversalSearchOptionType.Other
> = [
  UniversalSearchOptionType.Address,
  UniversalSearchOptionType.Parcel,
  UniversalSearchOptionType.Other,
]

/**
 * Appends universal-search entity hits (Address, Parcel, Other) to the result list.
 * Other layer rows use the configured layer name as the small title line.
 */
export const appendUniversalSearchEntityOptions = (
  results: UniversalSearchOption[],
  entitiesByType: UniversalSearchEntitiesByType
) => {
  for (const searchOptionType of entitySearchOptionTypes) {
    const entities = entitiesByType[entityResultsKeyBySearchOptionType[searchOptionType]] ?? []
    const layerType = layerTypeBySearchOptionType[searchOptionType]

    for (const entity of entities) {
      const url = entityUrl(entity)
      if (!url) {
        continue
      }

      const displaySubtitle = getUniversalSearchEntitySubtitle(entity, layerType)
      // Other hits: small line above = layer name, large line below = entity name (matches result template layout).
      const isOtherLayerEntity = searchOptionType === UniversalSearchOptionType.Other
      results.push({
        type: searchOptionType,
        title: isOtherLayerEntity ? displaySubtitle : entity.name,
        subtitle: isOtherLayerEntity ? entity.name : displaySubtitle,
        subtitleSortString: isOtherLayerEntity ? entity.name : displaySubtitle,
        url,
        uuid: uuid(),
      })
    }
  }
}
